// src/pages/UserPage/sections/OrganizationDeliveries/dashboard/InvoicesDashboard.jsx
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Chip, Divider, Stack, Tooltip, Collapse,
} from '@mui/material';
import { ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { BILLING_STATUS, getBillingStatus, fmtDateOnly } from './constants';
import { SenderModal } from './SenderModal';

function fmtEur(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(2)} €`;
}

// ─── Stat block ───────────────────────────────────────────────────────────────

const StatBlock = ({ label, children, minWidth = 80, onClick, active = false }) => (
  <Box
    sx={{
      minWidth,
      cursor: onClick ? 'pointer' : 'default',
      borderRadius: 1,
      px: onClick ? 0.5 : 0,
      mx: onClick ? -0.5 : 0,
      py: 0.25,
      transition: 'background 0.15s',
      ...(onClick && { '&:hover': { bgcolor: 'action.hover' } }),
      ...(active && { bgcolor: 'action.selected' }),
    }}
    onClick={onClick}
  >
    <Stack direction="row" alignItems="center" spacing={0.4} mb={0.25}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontSize: '0.65rem',
          color: active ? 'text.secondary' : 'text.disabled',
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>
      {onClick && (
        active
          ? <ChevronUp size={12} style={{ color: 'var(--mui-palette-text-disabled, #9e9e9e)' }} />
          : <ChevronDown size={12} style={{ color: 'var(--mui-palette-text-disabled, #9e9e9e)' }} />
      )}
    </Stack>
    {children}
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const InvoicesDashboard = ({
  deliveries = [],
  members = [],
  organizationId,
  hiddenDeliveries = [],
  showOwnerDeliveries,
  onToggleOwnerDeliveries,
  onStatusUpdate,
}) => {
  const [sendersExpanded, setSendersExpanded] = useState(false);
  const [modalGroup, setModalGroup]           = useState(null);

  const dates = useMemo(
    () => deliveries.map((d) => d.completedAtMs).filter(Boolean).sort((a, b) => a - b),
    [deliveries],
  );
  const earliest = dates[0];
  const latest   = dates[dates.length - 1];

  const totalValue = useMemo(
    () => deliveries.reduce((s, d) => s + (Number(d.price) || 0), 0),
    [deliveries],
  );

  const statusCounts = useMemo(
    () => Object.keys(BILLING_STATUS).reduce((acc, status) => {
      acc[status] = deliveries.filter((d) => getBillingStatus(d) === status).length;
      return acc;
    }, {}),
    [deliveries],
  );

  const senderGroups = useMemo(() => {
    const groups = {};
    for (const d of deliveries) {
      const member = members.find((m) => m.userId === d.senderId);
      const billingYNumber     = d.billing?.yNumber     || member?.billing?.yNumber     || null;
      const billingCompanyName = d.billing?.companyName || member?.billing?.companyName || member?.name || d.senderName || '—';
      const key = billingYNumber || d.senderId || `unknown-${d.id}`;
      if (!groups[key]) {
        groups[key] = { companyName: billingCompanyName, yNumber: billingYNumber, count: 0, total: 0, deliveries: [] };
      }
      groups[key].count += 1;
      groups[key].total += Number(d.price) || 0;
      groups[key].deliveries.push(d);
    }
    return Object.values(groups).sort((a, b) => b.total - a.total);
  }, [deliveries, members]);

  const dateLabel = useMemo(() => {
    if (!earliest && !latest) return '—';
    if (!latest || earliest === latest) return fmtDateOnly(earliest);
    return `${fmtDateOnly(earliest)} – ${fmtDateOnly(latest)}`;
  }, [earliest, latest]);

  const hiddenCount = hiddenDeliveries.length;

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          px: 2.5,
          py: 1.75,
          mb: 2.5,
          bgcolor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        {/* ── Key stats row ── */}
        <Stack
          direction="row"
          spacing={0}
          divider={<Divider orientation="vertical" flexItem sx={{ mx: 2.5 }} />}
          alignItems="flex-start"
          flexWrap="wrap"
          gap={1}
        >
          <StatBlock label="Aikaväli" minWidth={160}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
              {dateLabel}
            </Typography>
          </StatBlock>

          <StatBlock label="Toimituksia">
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                {deliveries.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {fmtEur(totalValue)}
              </Typography>
            </Stack>
          </StatBlock>

          <StatBlock label="Tila">
            <Stack direction="row" spacing={0.75} flexWrap="wrap">
              {Object.entries(BILLING_STATUS).map(([status, { label, color }]) => {
                const count = statusCounts[status];
                if (!count) return null;
                return (
                  <Tooltip key={status} title={label}>
                    <Chip
                      label={`${label} ${count}`}
                      size="small"
                      color={color}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          </StatBlock>

          {senderGroups.length > 0 && (
            <StatBlock
              label="Laskutettavia"
              onClick={() => setSendersExpanded((v) => !v)}
              active={sendersExpanded}
            >
              <Stack direction="row" spacing={0.5} alignItems="baseline">
                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                  {senderGroups.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">lähettäjää</Typography>
              </Stack>
            </StatBlock>
          )}

          {hiddenCount > 0 && (
            <StatBlock
              label="Piilotettu"
              onClick={onToggleOwnerDeliveries}
              active={showOwnerDeliveries}
              minWidth={80}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                {showOwnerDeliveries
                  ? <Eye size={14} style={{ opacity: 0.6 }} />
                  : <EyeOff size={14} style={{ opacity: 0.4 }} />
                }
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: '0.82rem', color: showOwnerDeliveries ? 'text.primary' : 'text.disabled' }}
                >
                  {hiddenCount}
                </Typography>
              </Stack>
            </StatBlock>
          )}
        </Stack>

        {/* ── Expandable sender cards ── */}
        <Collapse in={sendersExpanded}>
          <Divider sx={{ mt: 1.5, mb: 1.25 }} />
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {senderGroups.map((g, i) => (
              <Box
                key={g.yNumber || g.companyName || i}
                onClick={() => setModalGroup(g)}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 0.75,
                  minWidth: 140,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  noWrap
                  sx={{ display: 'block', maxWidth: 180, fontSize: '0.75rem' }}
                >
                  {g.companyName}
                </Typography>
                {g.yNumber && (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                    {g.yNumber}
                  </Typography>
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.25}>
                  <Typography variant="caption" color="text.secondary">{g.count} kpl</Typography>
                  <Typography variant="caption" fontWeight={700}>{fmtEur(g.total)}</Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Collapse>
      </Box>

      {modalGroup && (
        <SenderModal
          group={modalGroup}
          organizationId={organizationId}
          onStatusUpdate={onStatusUpdate}
          onClose={() => setModalGroup(null)}
        />
      )}
    </>
  );
};

export default InvoicesDashboard;
